import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { FiChevronLeft, FiCheck, FiLoader, FiEye, FiEyeOff } from "react-icons/fi"
import { RxCross2 } from "react-icons/rx";
import { useDebouncedCallback } from "use-debounce";

import CLink from "../components/CLink";
import "../styles/Signup.scss";
import axios from "../scripts/axiosConfig.js";
import { useAuth } from "../contexts/AuthContext.jsx";
import { useMisc } from "../contexts/MiscContext.jsx";

export default function Signup() {

    const { SIGN_UP_WITH_EMAIL, signupInProgress, setSignupInProgress } = useAuth();
    const { readableError } = useMisc();

    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const email = searchParams.get("email");

    const [signupProgress, setSignupProgress] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    // for now, soon will be changed to vars from useQuery.
    const [emailCheckStatus, setEmailCheckStatus] = useState<"loading" | "valid" | "invalid" | "taken" | "empty">("empty");
    const [passwordCheckResult, setPasswordCheckResult] = useState<"valid" | "invalid" | "empty">("empty");
    const [confirmPasswordCheckResult, setConfirmPasswordCheckResult] = useState<"mismatch" | "match" | "empty">("empty");
    const [usernameCheckResult, setUsernameCheckResult] = useState<"loading" | "valid" | "invalid" | "taken" | "">("");

    const [passwordShown, setPasswordShown] = useState<boolean>(false);

    const emailInputRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const confirmPasswordRef = useRef<HTMLInputElement>(null);
    const usernameRef = useRef<HTMLInputElement>(null);
    const displayNameRef = useRef<HTMLInputElement>(null);

    const isEmail = (email: string): boolean => {
        let regex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

        if (email.match(regex)) 
            return true; 

        else 
            return false; 
    }


    const validateInput = useDebouncedCallback( async (value, type) => {
        console.log(`Validating ${type}`);

        switch (type) {
            case "email":
                setEmailCheckStatus("loading");
                const email = value;
                if (isEmail(email)) {
                    const res = await axios.get(`/check_availability/email?email=${email}`) 
                    setEmailCheckStatus( res.data.available ? "valid" : "taken");
                } else {
                    setEmailCheckStatus("invalid");
                }
                break;

            case "username":
                setUsernameCheckResult("loading");
                const username = value;
                if (username.length < 4) {
                    setUsernameCheckResult("invalid");
                } else {
                    var res: any = await axios.get(`/check_availability/username?username=${username}`) 
                    console.log(res);
                    if (res.data.available === false) {
                        setUsernameCheckResult("taken");
                    }
                    else {
                        setUsernameCheckResult("valid");
                    }
                }
                break;
        
            default:
                break;
        };
    }, 500);

    
    const validatePassword = useDebouncedCallback(() => {
        if ( passwordRef?.current?.value ) {
            if (passwordRef.current.value.length < 6 || passwordRef.current.value.length > 18) {
                setPasswordCheckResult("invalid");
            } else {
                setPasswordCheckResult("valid");
            }
        }
        
        if ( confirmPasswordRef?.current?.value ) {
            if (confirmPasswordRef.current.value !== passwordRef?.current?.value) {
                setConfirmPasswordCheckResult("mismatch");
            } else {
                setConfirmPasswordCheckResult("match");
            }
        }
        else {
            setConfirmPasswordCheckResult("empty");
        }
    }, 100);
    

    const proceedToPasswordPage = () => {
        
        
        setCurrentPage(2);

    }

    const completeSignup = () => {
        setSignupInProgress(true);
        const email = emailInputRef.current?.value;
        const password = passwordRef.current?.value;
        const confirmPassword = confirmPasswordRef.current?.value;
        const username = usernameRef.current?.value;
        const displayName = displayNameRef.current?.value;

        console.table(
            [
                emailCheckStatus,
                passwordCheckResult,
                confirmPasswordCheckResult,
                usernameCheckResult,
            ]
        );


        if (!email || !password || !confirmPassword || !displayName) {
            setSignupInProgress(false);
            toast.error("One or more fields are missing during signup.");
            return;
        }

        setSignupProgress(20);


        SIGN_UP_WITH_EMAIL(email, password)
        .then((authRes: any) => {
            setSignupProgress(60);

            axios.post("/users/signup_email", {
                uid: authRes.user.uid,
                email: authRes.user.email,
                username,
                displayName,
            })
            .then((dbRes) => {
                setSignupProgress(100);
                setSignupInProgress(false);
                console.log("Account creation successful.");
                
                if (location.state.from) {
                    navigate(location.state.from === "/login" ? "/explore" : location.state.from);
                } else {
                    navigate("/explore");
                }

            })
            .catch((dbErr) => {
                console.log("db Err");
                setSignupProgress(0);
                setSignupInProgress(false);
                toast.error( readableError(dbErr) );
            });
        })
        .catch((authErr: any) => {
            setSignupProgress(0);
            setSignupInProgress(false);
            toast.error( readableError(authErr.code) );
        })
    }

    useEffect(() => {
        if (email) {
            validateInput(email, "email")
        }
    }, [])

    return (
        <div className="Signup">

            <div className="signupBox">

                <div className="welcomeText">
                    <span className="top">Welcome to <span className="c0difyText">c0dify!</span></span>
                    <span className="bottom"><span className="signUp">Create an account</span> to get started.</span>
                </div>
                <div className="nextPagesText">
                    <span className="c0difyText">c0dify</span> - Sign Up
                </div>

                <div className="signupStepContainer">
                    {/* ======================================================================================================= */}
                    <div className={("signupStep firstStep " + (currentPage === 1 ? "active" : "") )}>
                        <div className="inputContainer">
                            <span className="label">Enter email</span>
                            <input 
                                type="email" 
                                placeholder="tylerdurden@paperst.com"
                                ref={emailInputRef} 
                                onChange={(e) => validateInput(e.target.value, "email")}
                                defaultValue={email!}
                                autoComplete='new-off'
                                disabled={!(currentPage === 1)}
                            />
                            <span className={`statusIndicator emailStatus ${emailCheckStatus}`}>
                                {(emailCheckStatus === "loading") && <FiLoader />}
                                {(emailCheckStatus === "valid") && <FiCheck />}
                                {(emailCheckStatus === "invalid") && <RxCross2 />}
                                {(emailCheckStatus === "taken") && <RxCross2 />}
                            </span>
                            <div className={`emailErrorBox ${ ( emailCheckStatus === "taken" ) ? "visible" : "" }`}>
                                <>Email is already in use. <CLink to={`/login?email=${emailInputRef.current?.value}`}> Login?</CLink></>
                            </div>
                        </div>
                        <button 
                            className="nextBtn" 
                            disabled={ !!(emailCheckStatus && emailCheckStatus !== "valid") } 
                            onClick={proceedToPasswordPage}
                        >
                            Next
                        </button>
                    </div>

                    {/* ======================================================================================================= */}

                    <div className={("signupStep secondStep " + (currentPage === 2 ? "active" : "") )}>
                        
                        <button className="backButton" onClick={() => setCurrentPage(1)}><FiChevronLeft /></button>
                        <span className="title">
                            <div className="top">Step 2/3</div>
                            <div className="bottom">Choose a strong password</div>
                        </span>
                        
                        <div className="inputContainer">
                            <span className="label">Enter your password *</span>
                            <input 
                                type={passwordShown ? "text" : "password"} 
                                className="passwordInput password1" 
                                placeholder="Enter password" 
                                ref={passwordRef} 
                                autoComplete='new-password'
                                onChange={ (e) => validatePassword()}
                                minLength={6}
                                maxLength={18}
                                disabled={!(currentPage === 2)}
                            />
                            <div className={`passwordErrorBox forPassword1 ${ ( passwordCheckResult === "invalid" ) ? "visible" : "" }`}>
                                Password should contain 6 - 18 characters.
                            </div>
                            <span className={`statusIndicator passwordVisibilityToggle`} onClick={() => setPasswordShown((passwordShown) => !passwordShown) }>
                                {
                                    (passwordShown) ? <FiEyeOff /> : <FiEye />
                                }
                            </span>
                        </div>
                        <div className="inputContainer">
                            <span className="label">Enter password again *</span>
                            <input 
                                type="password" 
                                className="passwordInput password2" 
                                placeholder="Confirm password" 
                                ref={confirmPasswordRef} 
                                autoComplete='new-password'
                                onChange={ (e) => validatePassword()}
                                minLength={6}
                                maxLength={18}
                                disabled={!(currentPage === 2)}
                            />
                            <div className={`passwordErrorBox forPassword2 ${ ( confirmPasswordCheckResult === "mismatch" ) ? "visible" : "" }`}>
                                Should match original password.
                            </div>
                        </div>

                        <button 
                            className="nextBtn" 
                            onClick={() => setCurrentPage(3)} 
                            disabled={ 
                                (passwordCheckResult === "invalid" || confirmPasswordCheckResult === "mismatch" || 
                                    passwordCheckResult === "empty" || confirmPasswordCheckResult === "empty" ) }
                        >Next</button>
                    </div>

                    {/* ======================================================================================================= */}

                    <div className={("signupStep thirdStep " + (currentPage === 3 ? "active" : "") )}>
                        <button className="backButton" onClick={() => setCurrentPage(2)}><FiChevronLeft /></button>
                        <span className="title">
                            <div className="top">Step 3/3</div>
                            <div className="bottom">Customize your profile</div>
                        </span>
                        
                        <div className="inputContainer">
                            <span className="label">Username</span>
                            <input 
                                type="text" 
                                className={`usernameInput ${ ( usernameCheckResult === "invalid" ) && "invalid" }`}
                                placeholder="Enter username" 
                                ref={usernameRef} 
                                onChange={ (e) => validateInput(e.target.value, "username")}
                                minLength={4}
                                maxLength={20}
                                autoComplete='new-off'
                                disabled={!(currentPage === 3)}
                            />
                            <div className={`usernameErrorBox ${ ( usernameCheckResult === "invalid" || usernameCheckResult === "taken" ) ? "visible" : "" }`}>
                                {(usernameCheckResult === "invalid") && "Username should be atleast 4 characters long."}
                                {(usernameCheckResult === "taken") && "Username already taken."}
                            </div>
                            <span 
                            className={`statusIndicator usernameStatusCircle ${usernameCheckResult}`}>
                                {(usernameCheckResult === "loading") && <FiLoader />}
                                {(usernameCheckResult === "valid") && <FiCheck />}
                            </span>
                            <span className="label">Display Name *</span>
                            <input 
                                type="text" 
                                placeholder="Enter display name" 
                                ref={displayNameRef} 
                                minLength={1}
                                maxLength={28}
                                required
                                autoComplete='new-off'
                                disabled={!(currentPage === 3)}
                            />
                        </div>
                        
                        <button 
                            className={`signupBtn nextBtn ${(signupInProgress) ? 'loading' : ''}` }
                            onClick={completeSignup}
                            style={{ "--progress": `${signupProgress}%` } as React.CSSProperties}
                        >
                            <span>Sign Up</span>
                        </button>
                    </div>
                </div>

                <CLink className="goToLogin" to={"/login"}>
                    Already have an account?
                </CLink>

            </div>
        </div>
    )
}